/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { PieChart } from "../../models/http/charts/pie-chart/pie-chart.model";
import {
	ServiceNowCategoriesRequest,
	ServiceNowManufacturersRequest
} from "../../models/http/service-now/service-now-requests.model";
import { ServiceNowGeneralInformation } from "../../models/http/service-now/service-now-general-information.model";
import { ServiceNowManufacturerTable } from "../../models/http/service-now/service-now-manufacturer-table.model";
import { HardwareAssetsScopeMode } from "../../models/ui/hardware-assets-scope-mode.model";
import { HardwareAssetsScopeOrderBy } from "../../models/ui/hardware-assets-scope-orderby.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";
import { CommunicationService } from "../communication.service";

@Injectable({
	providedIn: "root"
})
export class DashboardServiceNowHttpService {
	private _dashboardServiceNowOperations = ENDPOINT_GROUPS.dashboardServiceNow.operations;

	constructor(private _communicationService: CommunicationService) {}

	public getGeneralInformation(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<ServiceNowGeneralInformation> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getGeneralInformation.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getDevicesCategories(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<{ categories: string[] }> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getDevicesCategories.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getDeviceCarbonFootprint(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<BarChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getDeviceCarbonFootprint.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public postScopeCarbonFootprintBar(
		tenantId: string,
		startDate: string,
		endDate: string | undefined,
		mode: HardwareAssetsScopeMode,
		orderBy?: HardwareAssetsScopeOrderBy,
		body?: ServiceNowManufacturersRequest
	): Observable<BarChart> {
		const params = new HttpParams({
			fromObject: {
				tenantId,
				startDate,
				...(endDate && { endDate }),
				mode,
				...(orderBy && { orderBy })
			}
		});

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.postScopeCarbonFootprintBar.service,
			HttpMethods.POST,
			{ params },
			body
		);
	}

	public getScopeCarbonFootprintTable(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<{ manufacturers: string[] }> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getScopeCarbonFootprintTable.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public postManufacturerEmissionPie(
		tenantId: string,
		startDate: string,
		endDate: string | undefined,
		body?: ServiceNowCategoriesRequest
	): Observable<PieChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.postManufacturerEmissionPie.service,
			HttpMethods.POST,
			{ params },
			body
		);
	}

	public getDeviceStatusEmissionPie(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<PieChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getDeviceStatusEmissionPie.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getDevicesModelsBar(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<BarChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getDevicesModelsBar.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getDevicesModelsBarPerDevice(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<BarChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getDevicesModelsBarPerDevice.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getDevicesLocationBar(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<BarChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getDevicesLocationBar.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getDevicesLocationBarPerDevice(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<BarChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getDevicesLocationBarPerDevice.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getTotalDevicesOverTime(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<LineChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getTotalDevicesOverTime.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getLocationDevicesOverTime(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<LineChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getLocationDevicesOverTime.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getLifecycleSavingsDevicesOverTime(
		tenantId: string,
		startDate: string,
		endDate?: string
	): Observable<LineChart> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getLifecycleSavingsDevicesOverTime.service,
			HttpMethods.GET,
			{ params }
		);
	}

	public getManufacturerEmissionTable(
		tenantId: string,
		startDate: string,
		endDate: string | undefined,
		body?: ServiceNowCategoriesRequest
	): Observable<ServiceNowManufacturerTable> {
		const params = this._buildDateParams(tenantId, startDate, endDate);

		return this._communicationService.callService(
			this._dashboardServiceNowOperations.getManufacturerEmissionTable.service,
			HttpMethods.POST,
			{ params },
			body
		);
	}

	private _buildDateParams(tenantId: string, startDate: string, endDate?: string): HttpParams {
		return new HttpParams({
			fromObject: {
				tenantId,
				startDate,
				...(endDate && { endDate })
			}
		});
	}
}
